import { useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowRight, BarChart3, Layers, Loader2, Users } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useI18n } from '@/hooks/useI18n';
import { translate } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BrandMark } from '@/components/primitives/BrandMark';
import { TextReveal } from '@/components/primitives/TextReveal';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api';

const schema = z.object({
  username: z.string().min(1, 'required'),
  password: z.string().min(1, 'required'),
});
type FormValues = z.infer<typeof schema>;

export function LoginScreen() {
  const { t } = useI18n();
  const { login, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (!isInitializing && isAuthenticated) return <Navigate to="/" replace />;
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      await login(values);
      navigate(from, { replace: true });
    } catch (err) {
      const apiErr = ApiError.from(err);
      toast.error(translate('Connexion impossible', 'Unable to sign in'), {
        description: apiErr.isAuth()
          ? translate('Identifiants incorrects', 'Incorrect credentials')
          : apiErr.localizedMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center gap-6 bg-bg-0 px-4 py-24 sm:px-6">
      <div className="absolute right-4 top-5 sm:right-6 sm:top-6">
        <LanguageSwitcher />
      </div>

      <div className="grid w-full max-w-[1040px] overflow-hidden rounded-[24px] border border-line-1 bg-bg-1 shadow-card md:grid-cols-[1.1fr_1fr]">
        <div className="relative hidden flex-col bg-violet-soft/60 p-9 md:flex lg:p-11">
          <div className="flex items-center gap-2.5">
            <BrandMark />
            <span className="text-[16px] font-semibold tracking-tight text-text-1">
              IG Analytics
            </span>
          </div>
          <TextReveal className="relative z-10 mt-12">
            <p className="t-stagger-line mb-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-violet-deep">
              {t('Des données à la décision', 'From data to decisions')}
            </p>
            <h2 className="t-stagger-line font-display max-w-sm text-[38px] leading-[1.12] text-text-1">
              {t('Vos comptes.', 'Your accounts.')}
              <br />
              {t('Une perspective', 'A clearer')}
              <br />
              <span className="text-violet-deep">{t('plus claire.', 'perspective.')}</span>
            </h2>
            <p className="t-stagger-line t-stagger-line--2 mt-4 max-w-xs text-[13px] leading-relaxed text-text-2">
              {t(
                'Réunissez vos comptes, suivez vos performances et repérez ce qui fonctionne.',
                'Bring your accounts together, track performance and discover what works.',
              )}
            </p>
          </TextReveal>
          <div className="relative flex h-60 items-center justify-center" aria-hidden="true">
            <span className="absolute size-48 rounded-full border border-violet/10" />
            <span className="absolute size-64 rounded-full border border-violet/5" />
            <img
              src="/images/analytics-sculpture.webp"
              alt=""
              width="768"
              height="768"
              className="studio-artwork relative size-72 object-contain"
            />
          </div>
          <div className="mt-auto flex flex-wrap gap-x-4 gap-y-2 border-t border-violet/10 pt-5 text-[10px] font-medium text-text-2">
            <span className="inline-flex items-center gap-1.5">
              <Users className="size-3 text-violet-deep" />
              {t('Comptes', 'Accounts')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <BarChart3 className="size-3 text-violet-deep" />
              Analytics
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Layers className="size-3 text-violet-deep" />
              Templates
            </span>
          </div>
        </div>

        <main className="flex min-w-0 flex-col justify-center px-6 py-9 sm:px-10 md:py-12 lg:px-12">
          <div className="mb-10 flex items-center gap-2.5 md:hidden">
            <BrandMark />
            <span className="text-[16px] font-semibold tracking-tight text-text-1">
              IG Analytics
            </span>
          </div>
          <span
            className="mb-5 flex size-11 items-center justify-center rounded-xl border border-line-1 bg-bg-0 text-violet-deep"
            aria-hidden="true"
          >
            <BarChart3 className="size-5" strokeWidth={1.7} />
          </span>
          <h1 className="font-display text-[26px] text-text-1">
            {t('Ravi de vous retrouver.', 'Welcome back.')}
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-text-3">
            {t(
              'Connectez-vous à votre espace de pilotage.',
              'Sign in to your analytics workspace.',
            )}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 flex flex-col gap-5">
            <Field
              id="login-username"
              label={t('Identifiant', 'Username')}
              error={errors.username ? t('Requis', 'Required') : undefined}
            >
              <Input
                id="login-username"
                autoComplete="username"
                placeholder={t('Votre identifiant', 'Your username')}
                aria-invalid={Boolean(errors.username)}
                aria-describedby={errors.username ? 'login-username-error' : undefined}
                className="h-11 sm:h-11"
                {...register('username')}
              />
            </Field>
            <Field
              id="login-password"
              label={t('Mot de passe', 'Password')}
              error={errors.password ? t('Requis', 'Required') : undefined}
            >
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? 'login-password-error' : undefined}
                className="h-11 sm:h-11"
                {...register('password')}
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-1 w-full justify-between gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span>{t('Connexion…', 'Signing in…')}</span>
                  <Loader2 className="size-4 motion-safe:animate-spin" />
                </>
              ) : (
                <>
                  {t('Se connecter', 'Sign in')}
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </form>
          <p className="mt-7 border-t border-line-1 pt-5 text-[11px] leading-relaxed text-text-3">
            {t(
              'Un seul espace pour suivre l’essentiel de votre activité Instagram.',
              'One workspace to follow what matters across your Instagram activity.',
            )}
          </p>
        </main>
      </div>
      <p className="text-[10px] tracking-wide text-text-3">
        IG Analytics{' '}
        <span className="px-1 text-line-3" aria-hidden="true">
          /
        </span>{' '}
        {t('Votre activité, en perspective.', 'Your activity, in perspective.')}
      </p>
    </div>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-[12px] font-medium text-text-2">
        {label}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-[11px] text-rose">
          {error}
        </p>
      )}
    </div>
  );
}
